import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, faEye, faEdit, faCopy, faTrash, faPaperPlane, 
  faSearch, faFilter, faTimes, faHistory, faClipboardList, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { QuestionnaireTemplate } from '../types/questionnaire';
import { 
  getQuestionnaireTemplates, 
  deleteQuestionnaireTemplate, 
  duplicateTemplate
} from '../services/api';
import PreviewModal from '../components/questionnaires/PreviewModal';
import DeleteModal from '../components/questionnaires/DeleteModal';
import ForceDeleteModal from '../components/questionnaires/ForceDeleteModal';
import SendModal from '../components/questionnaires/SendModal';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import VersionHistory from '../components/questionnaires/VersionHistory';

// Zentrale Konfiguration der Fragebogen-Typen (Emoji + Label an einer Stelle)
const TEMPLATE_TYPES: { value: string; emoji: string; label: string }[] = [
  { value: 'producer', emoji: '🟢', label: 'Produzent' },
  { value: 'trader', emoji: '🟠', label: 'Händler' },
  { value: 'trader_producer', emoji: '🟣', label: 'Händler & Produzent' },
  { value: 'non_food', emoji: '🔵', label: 'Non-Food' },
  { value: 'gmo_declaration', emoji: '🧬', label: 'GVO-Erklärung' },
  { value: 'conformity_declaration', emoji: '📋', label: 'Konformitätserklärung' },
  { value: 'emergency_contact', emoji: '🚨', label: 'Notfallkontakt' },
];
const TEMPLATE_TYPE_MAP: { [key: string]: { emoji: string; label: string } } =
  Object.fromEntries(TEMPLATE_TYPES.map((t) => [t.value, t]));

// Farb-Akzente pro Typ (statische Klassen, damit Tailwind sie beim Build behält)
const TYPE_ACCENT: { [key: string]: { bar: string; badge: string; dot: string } } = {
  producer:               { bar: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-100', dot: 'bg-emerald-500' },
  trader:                 { bar: 'bg-amber-500',   badge: 'bg-amber-50 text-amber-700 border-amber-100',     dot: 'bg-amber-500' },
  trader_producer:        { bar: 'bg-violet-500',  badge: 'bg-violet-50 text-violet-700 border-violet-100',  dot: 'bg-violet-500' },
  non_food:               { bar: 'bg-blue-500',    badge: 'bg-blue-50 text-blue-700 border-blue-100',        dot: 'bg-blue-500' },
  gmo_declaration:        { bar: 'bg-teal-500',    badge: 'bg-teal-50 text-teal-700 border-teal-100',        dot: 'bg-teal-500' },
  conformity_declaration: { bar: 'bg-slate-400',   badge: 'bg-slate-100 text-slate-600 border-slate-200',    dot: 'bg-slate-400' },
  emergency_contact:      { bar: 'bg-red-500',     badge: 'bg-red-50 text-red-700 border-red-100',           dot: 'bg-red-500' },
};
const DEFAULT_ACCENT = { bar: 'bg-slate-300', badge: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-300' };

// Wiederverwendbares, modern gestyltes Dropdown mit eigenem Chevron (kein nativer Pfeil)
const StyledSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  className?: string;
  children: React.ReactNode;
}> = ({ value, onChange, className, children }) => (
  <div className={`relative ${className || ''}`}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none w-full pl-3.5 pr-9 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 shadow-sm hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400 transition-colors cursor-pointer"
    >
      {children}
    </select>
    <svg
      className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  </div>
);

const Questionnaires: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [templates, setTemplates] = useState<QuestionnaireTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>((location.state as any)?.filterType || 'all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [highlightId, setHighlightId] = useState<string | undefined>((location.state as any)?.highlightId);
  
  // Modal States
  const [showPreview, setShowPreview] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<QuestionnaireTemplate | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
  const [deleteWarningData, setDeleteWarningData] = useState<any>(null);
  const [templateToDelete, setTemplateToDelete] = useState<QuestionnaireTemplate | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [selectedTemplateForVersions, setSelectedTemplateForVersions] = useState<QuestionnaireTemplate | null>(null);



  // Data loading - nur Templates laden
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const templatesResponse = await getQuestionnaireTemplates();
      setTemplates(templatesResponse.data || []);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reload templates when returning from editor
  useEffect(() => {
    if (location.state) {
      loadData();
      // Clear location state after using it
      window.history.replaceState({}, document.title);
    }
  }, [location.state, loadData]);

  // Event handlers
  const handlePreview = useCallback((template: QuestionnaireTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedTemplate(template);
    setShowPreview(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setShowPreview(false);
    setTimeout(() => setSelectedTemplate(null), 200);
  }, []);

  const handleSendClick = useCallback((template: QuestionnaireTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedTemplate(template);
    setShowSendModal(true);
  }, []);

  const handleCloseSendModal = useCallback(() => {
    setShowSendModal(false);
    setTimeout(() => setSelectedTemplate(null), 200);
  }, []);

  const handleDelete = useCallback((template: QuestionnaireTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    
    // Prüfen ob es ein nicht löschbares System-Template ist
    if (template.isSystemTemplate || template.isDeletable === false) {
      return;
    }
    
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setShowForceDeleteModal(false);
    setDeleteWarningData(null);
    setTimeout(() => setTemplateToDelete(null), 200);
  }, []);

  const handleConfirmDelete = useCallback(async (forceDelete = false) => {
    if (!templateToDelete || !templateToDelete._id) return;

    try {
      const url = forceDelete ? `${templateToDelete._id}?force=true` : templateToDelete._id;
      await deleteQuestionnaireTemplate(url);
      await loadData();

      // Nach erfolgreichem Löschen alle Delete-Modals schließen
      setShowDeleteModal(false);
      setShowForceDeleteModal(false);
      setDeleteWarningData(null);
      setTemplateToDelete(null);
    } catch (error: any) {
      console.error('Error deleting template:', error);

      // 409 Conflict = es existieren noch Submissions -> Force-Delete-Modal anbieten
      if (error.response?.status === 409) {
        setShowDeleteModal(false);
        setDeleteWarningData(error.response.data);
        setShowForceDeleteModal(true);
      } else {
        setError('Fehler beim Löschen des Fragebogens');
      }
    }
  }, [templateToDelete, loadData]);

  const handleDuplicate = useCallback(async (template: QuestionnaireTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await duplicateTemplate(template._id!);
      await loadData();
    } catch (error) {
      console.error('Error duplicating template:', error);
    }
  }, [loadData]);

  const handleShowVersionHistory = useCallback((template: QuestionnaireTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedTemplateForVersions(template);
    setShowVersionHistory(true);
  }, []);

  const handleCloseVersionHistory = useCallback(() => {
    setShowVersionHistory(false);
    setTimeout(() => setSelectedTemplateForVersions(null), 200);
  }, []);

  const handleVersionRestored = useCallback((newTemplate: QuestionnaireTemplate) => {
    loadData();
    setShowVersionHistory(false);
    setTimeout(() => setSelectedTemplateForVersions(null), 200);
  }, [loadData]);

  const handleTemplateClick = useCallback((template: QuestionnaireTemplate) => {
    navigate(`/questionnaires/edit/${template._id}`);
  }, [navigate]);

  // Memoized helpers
  const getTypeEmoji = useCallback((type: string) => {
    return TEMPLATE_TYPE_MAP[type]?.emoji || '❓';
  }, []);

  // Get template title based on its language
  const getTemplateTitle = useCallback((template: QuestionnaireTemplate): string => {
    // Use the title in the template's language if available, otherwise fallback to DE
    if (template.language === 'en' && template.title.en) {
      return template.title.en;
    }
    return template.title.de || template.title.en || 'Untitled';
  }, []);

  // Get template description based on its language
  const getTemplateDescription = useCallback((template: QuestionnaireTemplate): string => {
    // Use the description in the template's language if available, otherwise fallback to DE
    if (template.language === 'en' && template.description.en) {
      return template.description.en;
    }
    return template.description.de || template.description.en || '';
  }, []);

  // Memoized filtered templates
  const filteredTemplates = useMemo(() => {
    let filtered = templates.filter(template => {
      const matchesSearch = getTemplateTitle(template).toLowerCase().includes(searchTerm.toLowerCase()) ||
                           getTemplateDescription(template).toLowerCase().includes(searchTerm.toLowerCase());
      
      // **SPECIAL HANDLING FOR SYSTEM TEMPLATES**
      // Only show conformity declarations when explicitly filtered for them
      if (template.type === 'conformity_declaration') {
        return matchesSearch && filterType === 'conformity_declaration';
      }
      
      // Only show emergency contacts when explicitly filtered for them
      if (template.type === 'emergency_contact') {
        return matchesSearch && filterType === 'emergency_contact';
      }
      
      // For regular templates, exclude system templates from "all" and other filters
      const matchesType = filterType === 'all' || template.type === filterType;
      const matchesStatus =
        filterStatus === 'all' ||
        (filterStatus === 'active' ? template.isActive : !template.isActive);

      return matchesSearch && matchesType && matchesStatus;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.createdAt!).getTime() - new Date(b.createdAt!).getTime();
        case 'name':
          return getTemplateTitle(a).localeCompare(getTemplateTitle(b));
        case 'questions':
          return b.questions.length - a.questions.length;
        case 'newest':
        default:
          return new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime();
      }
    });

    return filtered;
  }, [templates, searchTerm, filterType, filterStatus, sortBy, getTemplateTitle, getTemplateDescription]);

  if (loading) {
    return <LoadingSpinner size="medium" text="Fragebögen werden geladen..." />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* **TOOLBAR** */}
      <div className="bg-white border-b border-slate-200/70">
        <div className="px-5 py-4 max-w-[1700px] mx-auto w-full">
          <div className="flex flex-col xl:flex-row gap-3 xl:items-center">
            {/* **SUCHE** */}
            <div className="relative flex-1 min-w-[220px]">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none" />
              <input
                type="text"
                placeholder="Fragebögen durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-9 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 shadow-sm placeholder:text-slate-400 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400 transition-colors"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  title="Suche zurücksetzen"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-sm" />
                </button>
              )}
            </div>

            {/* **FILTER UND AKTIONEN** */}
            <div className="flex flex-wrap gap-2">
              <StyledSelect value={filterType} onChange={setFilterType} className="min-w-[170px]">
                <option value="all">Alle Typen</option>
                {TEMPLATE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.emoji} {t.label}
                  </option>
                ))}
              </StyledSelect>

              <StyledSelect value={filterStatus} onChange={setFilterStatus} className="min-w-[130px]">
                <option value="all">Alle Status</option>
                <option value="active">Aktiv</option>
                <option value="inactive">Inaktiv</option>
              </StyledSelect>

              <StyledSelect value={sortBy} onChange={setSortBy} className="min-w-[130px]">
                <option value="newest">Neueste</option>
                <option value="oldest">Älteste</option>
                <option value="name">Name</option>
                <option value="questions">Fragen</option>
              </StyledSelect>

              <Link
                to="/questionnaires/create"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-br from-[#e2313a] to-[#c9252d] text-white rounded-xl shadow-sm shadow-red-500/20 hover:shadow-md hover:brightness-105 transition-all text-sm font-semibold whitespace-nowrap"
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
                Neuer Fragebogen
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* **ERROR MESSAGE** */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.15 }}
            className="px-5 pt-4 max-w-[1700px] mx-auto w-full"
          >
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-xl flex items-center justify-between text-sm">
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-3 text-red-500 hover:text-red-700"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* **CONTENT BEREICH** */}
      <div className="p-5 max-w-[1700px] mx-auto w-full">
        {filteredTemplates.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200/70 shadow-sm">
            <div className="text-slate-400 mb-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 flex items-center justify-center">
                <FontAwesomeIcon icon={faClipboardList} className="text-2xl text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {searchTerm || filterType !== 'all' || filterStatus !== 'all' 
                  ? 'Keine Fragebögen gefunden' 
                  : 'Noch keine Fragebögen vorhanden'}
              </h3>
              <p className="text-slate-500 text-sm max-w-md mx-auto">
                {searchTerm || filterType !== 'all' || filterStatus !== 'all'
                  ? 'Versuchen Sie andere Suchkriterien oder Filter'
                  : 'Erstellen Sie Ihren ersten Fragebogen und beginnen Sie mit der Lieferantenbewertung'}
              </p>
            </div>
            
            {(!searchTerm && filterType === 'all' && filterStatus === 'all') && (
              <Link
                to="/questionnaires/create"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-br from-[#e2313a] to-[#c9252d] text-white rounded-xl shadow-sm hover:brightness-105 transition-all font-semibold"
              >
                <FontAwesomeIcon icon={faPlus} />
                Ersten Fragebogen erstellen
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {filteredTemplates.map((template, index) => {
              const isHighlighted = highlightId === template._id;
              const accent = TYPE_ACCENT[template.type] || DEFAULT_ACCENT;

              return (
                <motion.div
                  key={template._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0, scale: isHighlighted ? 1.02 : 1 }}
                  transition={{ delay: index * 0.03 }}
                  whileHover={{ y: -4 }}
                  className={`relative bg-white rounded-2xl shadow-sm border overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group flex flex-col ${
                    isHighlighted
                      ? 'border-red-400 ring-2 ring-red-100'
                      : 'border-slate-200/70 hover:border-slate-300'
                  }`}
                  onClick={() => handleTemplateClick(template)}
                  onAnimationComplete={() => {
                    // Remove highlight after animation
                    if (isHighlighted) {
                      setTimeout(() => setHighlightId(undefined), 2000);
                    }
                  }}
                >
                  {/* Farb-Akzent oben */}
                  <span className={`absolute top-0 left-0 right-0 h-1 ${accent.bar}`} />

                  <div className="p-4 pt-5 flex flex-col flex-1">
                    {/* **HEADER** */}
                    <div className="flex items-center flex-wrap gap-1.5 mb-2.5">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-full font-medium border ${accent.badge}`}>
                        <span aria-hidden="true">{getTypeEmoji(template.type)}</span>
                        {TEMPLATE_TYPE_MAP[template.type]?.label || template.type}
                      </span>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-full font-medium border ${
                        template.isActive
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                          : 'bg-slate-100 text-slate-500 border-slate-200'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${template.isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                        {template.isActive ? 'Aktiv' : 'Inaktiv'} · v{template.version}
                      </span>
                    </div>

                    <h3 className="font-semibold text-slate-900 text-sm mb-1 group-hover:text-red-600 transition-colors line-clamp-2">
                      {getTemplateTitle(template)}
                    </h3>
                    <p className="text-slate-500 text-xs line-clamp-2 mb-3 flex-1">
                      {getTemplateDescription(template)}
                    </p>

                    {/* **STATS** */}
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-3 pt-3 border-t border-slate-100">
                      <span className="inline-flex items-center gap-1.5">
                        <FontAwesomeIcon icon={faClipboardList} className="text-slate-400" />
                        <span className="font-semibold text-slate-700">
                          {template.questions.filter(q => q.type !== 'section').length}
                        </span>
                        Fragen
                      </span>
                      <span>
                        {new Date(template.createdAt || '').toLocaleDateString('de-DE')}
                      </span>
                    </div>

                  {/* **ACTIONS** */}
                  <div className="grid grid-cols-3 gap-1.5 mb-1.5">
                    <button
                      onClick={(e) => handlePreview(template, e)}
                      className="flex items-center justify-center px-2 py-1.5 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 font-medium transition-colors"
                      title="Vorschau"
                    >
                      <FontAwesomeIcon icon={faEye} className="mr-1 text-xs" />
                      Vorschau
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/questionnaires/edit/${template._id}`);
                      }}
                      className="flex items-center justify-center px-2 py-1.5 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 font-medium transition-colors"
                      title="Bearbeiten"
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-1 text-xs" />
                      Bearbeiten
                    </button>
                    
                    {/* Senden Button - nicht für Konformitätserklärungen */}
                    {template.type !== 'conformity_declaration' && (
                      <button
                        onClick={(e) => handleSendClick(template, e)}
                        className="flex items-center justify-center px-2 py-1.5 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100 font-medium transition-colors"
                        title="Senden"
                      >
                        <FontAwesomeIcon icon={faPaperPlane} className="mr-1 text-xs" />
                        Senden
                      </button>
                    )}
                    
                    {/* Placeholder für Konformitätserklärungen */}
                    {template.type === 'conformity_declaration' && (
                      <div className="flex items-center justify-center px-2 py-1.5 text-xs bg-gray-100 text-gray-400 rounded font-medium">
                        <FontAwesomeIcon icon={faShieldAlt} className="mr-1 text-xs" />
                        Geschützt
                      </div>
                    )}
                  </div>

                  {/* **SECONDARY ACTIONS** */}
                  <div className="grid grid-cols-3 gap-1.5">
                    <button
                      onClick={(e) => handleShowVersionHistory(template, e)}
                      className="flex items-center justify-center px-2 py-1.5 text-xs bg-orange-50 text-orange-700 rounded hover:bg-orange-100 font-medium transition-colors"
                      title="Versionen"
                    >
                      <FontAwesomeIcon icon={faHistory} className="mr-1 text-xs" />
                      Versionen
                    </button>
                    
                    {/* Kopieren Button - nicht für Konformitätserklärungen */}
                    {template.type !== 'conformity_declaration' && (
                      <button
                        onClick={(e) => handleDuplicate(template, e)}
                        className="flex items-center justify-center px-2 py-1.5 text-xs bg-gray-50 text-gray-700 rounded hover:bg-gray-100 font-medium transition-colors"
                        title="Duplizieren"
                      >
                        <FontAwesomeIcon icon={faCopy} className="mr-1 text-xs" />
                        Kopieren
                      </button>
                    )}
                    
                    {/* Placeholder für Konformitätserklärungen */}
                    {template.type === 'conformity_declaration' && (
                      <div className="flex items-center justify-center px-2 py-1.5 text-xs bg-gray-100 text-gray-400 rounded font-medium">
                        <FontAwesomeIcon icon={faShieldAlt} className="mr-1 text-xs" />
                        Geschützt
                      </div>
                    )}
                    
                    {/* Delete button - nur anzeigen wenn löschbar */}
                    {(!template.isSystemTemplate && template.isDeletable !== false) && (
                      <button
                        onClick={(e) => handleDelete(template, e)}
                        className="flex items-center justify-center px-2 py-1.5 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100 font-medium transition-colors"
                        title="Löschen"
                      >
                        <FontAwesomeIcon icon={faTrash} className="mr-1 text-xs" />
                        Löschen
                      </button>
                    )}
                    
                    {/* Placeholder wenn Delete-Button versteckt ist */}
                    {(template.isSystemTemplate || template.isDeletable === false) && (
                      <div className="flex items-center justify-center px-2 py-1.5 text-xs bg-gray-100 text-gray-400 rounded font-medium">
                        <FontAwesomeIcon icon={faShieldAlt} className="mr-1 text-xs" />
                        Geschützt
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
            })}
          </div>
        )}
      </div>

      {/* **MODALS** */}
      <AnimatePresence>
        {showPreview && selectedTemplate && (
          <PreviewModal
            template={selectedTemplate}
            language="de"
            onClose={handleClosePreview}
          />
        )}
        
        {showSendModal && selectedTemplate && (
          <SendModal
            template={selectedTemplate}
            onClose={handleCloseSendModal}
            onSuccess={loadData}
          />
        )}
        
        {showDeleteModal && templateToDelete && (
          <DeleteModal
            templateTitle={getTemplateTitle(templateToDelete)}
            onClose={handleCloseDeleteModal}
            onConfirm={() => handleConfirmDelete(false)}
          />
        )}
        
        {showForceDeleteModal && templateToDelete && deleteWarningData && (
          <ForceDeleteModal
            templateTitle={getTemplateTitle(templateToDelete)}
            warningData={deleteWarningData}
            onClose={handleCloseDeleteModal}
            onConfirm={() => handleConfirmDelete(true)}
          />
        )}
        
        {showVersionHistory && selectedTemplateForVersions && (
          <VersionHistory
            templateId={selectedTemplateForVersions._id!}
            templateTitle={getTemplateTitle(selectedTemplateForVersions)}
            currentVersion={selectedTemplateForVersions.version}
            onClose={handleCloseVersionHistory}
            onVersionRestored={handleVersionRestored}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Questionnaires;